import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Select } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import { FiPlus, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const AvailabilitySchema = z.object({
  matcherType: z.enum(['SpecifiedDays', 'MonTueWedThuFri', 'SatSun', 'EveryDay', 'USHoliday', 'UKHoliday']),
  capacity: z.coerce.number().min(0).max(10).int(),
  specifiedDays: z.string().optional(),
  after: z.string().optional(),
  before: z.string().optional(),
});

type FormValues = z.infer<typeof AvailabilitySchema>;

interface AvailabilityPolicyFormProps {
  onSubmit: (availability: wasm.PersonAvailability) => Promise<void>;
  className?: string;
}

function buildMatcher(values: FormValues): wasm.DayMatcher {
  switch (values.matcherType) {
    case 'SpecifiedDays': {
      const days = (values.specifiedDays || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      return { type: 'SpecifiedDays', days };
    }
    case 'MonTueWedThuFri':
      return { type: 'MonTueWedThuFri' } as const;
    case 'SatSun':
      return { type: 'SatSun' } as const;
    case 'EveryDay':
      return { type: 'EveryDay' } as const;
    case 'USHoliday':
      return { type: 'USHoliday' } as const;
    case 'UKHoliday':
      return { type: 'UKHoliday' } as const;
  }
}

function defaultCapacityFor(matcherType: string): number {
  switch (matcherType) {
    case 'USHoliday':
    case 'UKHoliday':
    case 'SpecifiedDays':
      return 0;
    default:
      return 10;
  }
}

function summarize(values: FormValues): string {
  const matcher: Record<string, string> = {
    MonTueWedThuFri: 'Mon\u2013Fri',
    SatSun: 'Sat\u2013Sun',
    EveryDay: 'every day',
    SpecifiedDays: 'specific dates',
    USHoliday: 'US holidays',
    UKHoliday: 'UK holidays',
  };
  const pct = values.capacity * 10;
  const cap = pct === 0 ? 'unavailable' : pct === 100 ? 'full capacity' : `${pct}% capacity`;
  return `On ${matcher[values.matcherType] ?? values.matcherType}, ${cap}`;
}

const AvailabilityPolicyForm: React.FC<AvailabilityPolicyFormProps> = ({ onSubmit, className }) => {
  const [showDates, setShowDates] = useState(false);

  const initialValues: FormValues = {
    matcherType: 'MonTueWedThuFri',
    capacity: 10,
    specifiedDays: '',
    after: '',
    before: '',
  };

  const handleSubmit = async (values: FormValues, { resetForm }: { resetForm: () => void }) => {
    const availability: wasm.PersonAvailability = {
      id: uuidv4(),
      after: values.after || null,
      before: values.before || null,
      policy: {
        matcher: buildMatcher(values),
        capacity: values.capacity,
      },
    };
    await onSubmit(availability);
    resetForm();
    setShowDates(false);
  };

  return (
    <div className={className}>
      <Formik
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(AvailabilitySchema)}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form>
            {/* Main row: readable sentence-like layout */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-cm-text-muted">On</span>
              <Field
                as={Select}
                id="matcherType"
                name="matcherType"
                sizing="sm"
                className="w-auto"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  setFieldValue('matcherType', val);
                  setFieldValue('capacity', defaultCapacityFor(val));
                }}
              >
                <option value="MonTueWedThuFri">Mon–Fri</option>
                <option value="SatSun">Sat–Sun</option>
                <option value="EveryDay">every day</option>
                <option value="SpecifiedDays">specific dates</option>
                <option value="USHoliday">US holidays</option>
                <option value="UKHoliday">UK holidays</option>
              </Field>

              <span className="text-xs text-cm-text-muted">,</span>

              <div className="flex items-center gap-1.5">
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="1"
                  value={values.capacity}
                  onChange={(e) => setFieldValue('capacity', parseInt(e.target.value))}
                  className="w-24 h-1 rounded-full appearance-none cursor-pointer bg-gray-700
                             [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3
                             [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full
                             [&::-webkit-slider-thumb]:bg-cm-accent-blue"
                />
                <span className={`text-xs font-mono w-8 ${values.capacity === 0 ? 'text-red-400' : 'text-cm-text-secondary'}`}>
                  {values.capacity * 10}%
                </span>
              </div>

              <button
                type="button"
                onClick={() => setShowDates(!showDates)}
                className="flex items-center gap-0.5 text-[10px] text-cm-text-muted hover:text-cm-text-secondary transition-colors"
              >
                {showDates ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
                {values.after || values.before ? 'dates set' : 'limit dates'}
              </button>

              <Button type="submit" size="xs" disabled={isSubmitting} className="ml-auto">
                <FiPlus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </div>

            {/* Specific dates input (conditional) */}
            {values.matcherType === 'SpecifiedDays' && (
              <div className="mt-2">
                <Field
                  as={TextInput}
                  id="specifiedDays"
                  name="specifiedDays"
                  sizing="sm"
                  placeholder="YYYY-MM-DD, YYYY-MM-DD, ..."
                />
                <ErrorMessage name="specifiedDays" component="div" className="text-red-500 text-xs mt-0.5" />
              </div>
            )}

            {/* Optional date bounds (collapsible) */}
            {showDates && (
              <div className="mt-2 grid grid-cols-2 gap-3 p-2 rounded-lg border border-cm-border-subtle bg-cm-bg-deep/50">
                <div>
                  <Label htmlFor="after" className="mb-1 block text-[10px] text-cm-text-muted">
                    Effective from (optional)
                  </Label>
                  <Field as={TextInput} id="after" name="after" type="date" sizing="sm" />
                </div>
                <div>
                  <Label htmlFor="before" className="mb-1 block text-[10px] text-cm-text-muted">
                    Effective until (optional)
                  </Label>
                  <Field as={TextInput} id="before" name="before" type="date" sizing="sm" />
                </div>
              </div>
            )}

            {/* Summary preview */}
            <p className="mt-1.5 text-[10px] text-cm-text-muted italic">{summarize(values)}</p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AvailabilityPolicyForm;
